/**
 * The Scenarios tab as the replay harness front end — PD-5 (spec §11:
 * "`ScenariosPage` becomes the replay harness front end").
 *
 * Three things, in the order an operator uses them:
 *
 *  1. **Launch.** The exact invocation for THIS scenario on either path, ready
 *     to copy. The admin cannot run a scenario itself — a run drives the local
 *     stack and real models from the operator's machine — and it does not
 *     pretend to; see `run-model.ts`.
 *  2. **Read.** Every run recorded for this scenario: path, outcome, the
 *     pairwise grade, and a link out to everything the run produced (the story,
 *     the pebble timeline, the match-events log, the match).
 *  3. **Compare.** Two runs — one per path — put side by side (`PathComparison`),
 *     and the run's own `extracted_v1` parity report, scoped to the pebbles that
 *     run created.
 *
 * A database without migration 033 has no run table. That is reported, in
 * words, where the runs would be: an empty list would read as "this scenario
 * has never been run", which is a different and much more misleading claim.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  deleteScenarioRun,
  getExtractedParity,
  listScenarioRuns,
} from '../../api/client';
import type {
  ExtractedParityReport,
  ScenarioRun,
  ScenarioRunPath,
  TestScenario,
} from '../../api/types';
import ParityReport from '../comparison/ParityReport';
import { mergeReports } from '../comparison/parity-model';
import PathComparison from './PathComparison';
import {
  RUN_PATHS,
  byStartedDesc,
  gradeVerdict,
  latestByPath,
  launchCommands,
  recordRunCommand,
  runDurationMs,
  runPebbleIds,
  runVerdict,
  tallyGrades,
  type RunVerdict,
} from './run-model';

interface Props {
  scenario: TestScenario;
}

const VERDICT_CLASS: Record<RunVerdict, string> = {
  passed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  running: 'bg-sky-100 text-sky-700',
  error: 'bg-orange-100 text-orange-800',
  ungraded: 'bg-gray-100 text-gray-600',
};

const PATH_CLASS: Record<ScenarioRunPath, string> = {
  legacy: 'bg-gray-100 text-gray-700',
  split: 'bg-indigo-100 text-indigo-700',
};

function CopyBlock({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <button
          type="button"
          className="text-xs text-pebbl-700 hover:underline"
          onClick={() => {
            // `navigator.clipboard` is absent in jsdom and in http:// origins;
            // the command is on screen either way, so a failed copy is a
            // non-event and must not throw into the render tree.
            void navigator.clipboard
              ?.writeText(text)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? 'copied' : 'copy'}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto whitespace-pre">
        {text}
      </pre>
    </div>
  );
}

function RunRow({
  run,
  onDelete,
  onParity,
  parityOpen,
}: {
  run: ScenarioRun;
  onDelete: (run: ScenarioRun) => void;
  onParity: (run: ScenarioRun) => void;
  parityOpen: boolean;
}) {
  const verdict = runVerdict(run);
  const tally = tallyGrades(run);
  const duration = runDurationMs(run);
  return (
    <div
      className="border border-gray-200 rounded-lg p-3 space-y-2"
      data-testid="scenario-run"
      data-run-id={run.id}
      data-verdict={verdict}
      data-path={run.path}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded ${PATH_CLASS[run.path] ?? 'bg-gray-100'}`}>
          {run.path}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded ${VERDICT_CLASS[verdict]}`}>{verdict}</span>
        <span className="text-sm text-gray-700">{run.startedAt}</span>
        {duration != null && (
          <span className="text-xs text-gray-500">{Math.round(duration / 1000)}s</span>
        )}
        {run.stack && (
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{run.stack}</span>
        )}
        {tally.total > 0 && (
          <span className="text-xs text-gray-600">
            {tally.passed}✓ {tally.failed}✗{tally.ungraded ? ` ${tally.ungraded}?` : ''} pairs
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            className="text-xs text-pebbl-700 hover:underline"
            onClick={() => onParity(run)}
          >
            {parityOpen ? 'hide parity' : 'parity for this run'}
          </button>
          <button
            type="button"
            className="text-xs text-red-600 hover:underline"
            onClick={() => onDelete(run)}
          >
            delete
          </button>
        </div>
      </div>

      {/* What the run produced, per side — every id a way into another screen. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {(run.sides ?? []).map((side) => (
          <span key={side.label} className="flex items-center gap-1.5" data-testid="run-side" data-label={side.label}>
            <span className="font-medium text-gray-700">{side.label}</span>
            {side.storyId ? (
              <Link to={`/stories/${side.storyId}`} className="text-pebbl-600 hover:underline">
                story →
              </Link>
            ) : (
              <span className="text-gray-400">no submit</span>
            )}
            {side.pebbleId && (
              <>
                <Link to={`/conversation/${side.pebbleId}`} className="text-pebbl-600 hover:underline">
                  timeline →
                </Link>
                <Link to={`/match-events/${side.pebbleId}`} className="text-pebbl-600 hover:underline">
                  events →
                </Link>
              </>
            )}
          </span>
        ))}
      </div>

      {/* The pairwise oracle result. */}
      {(run.grades ?? []).length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {run.grades.map((g, i) => {
            const pass = gradeVerdict(g);
            return (
              <span
                key={`${g.a}-${g.b}-${i}`}
                data-testid="run-grade"
                data-pass={pass === null ? 'unknown' : String(pass)}
                className={`px-2 py-0.5 rounded ${
                  pass === true
                    ? 'bg-emerald-50 text-emerald-700'
                    : pass === false
                      ? 'bg-rose-50 text-rose-700'
                      : 'bg-gray-100 text-gray-500'
                }`}
              >
                {g.a}↔{g.b}: expected {g.expectedMatch ? 'match' : 'no match'}, got{' '}
                {g.actualMatch == null ? 'nothing' : g.actualMatch ? 'match' : 'no match'}
                {g.matchId && (
                  <>
                    {' '}
                    <Link to={`/matches/${g.matchId}`} className="underline">
                      open
                    </Link>
                  </>
                )}
              </span>
            );
          })}
        </div>
      )}

      {run.docPath && (
        <p className="text-xs text-gray-500 font-mono" data-testid="run-doc">
          {run.docPath}
        </p>
      )}
      {run.notes && <p className="text-xs text-gray-600">{run.notes}</p>}
    </div>
  );
}

export default function ScenarioRuns({ scenario }: Props) {
  const [runs, setRuns] = useState<ScenarioRun[]>([]);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [legacyId, setLegacyId] = useState<string>('');
  const [splitId, setSplitId] = useState<string>('');
  const [showCompare, setShowCompare] = useState(false);
  const [parityRunId, setParityRunId] = useState<string | null>(null);
  const [parity, setParity] = useState<ExtractedParityReport | null>(null);
  const [parityError, setParityError] = useState<string | null>(null);
  const [parityLoading, setParityLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listScenarioRuns(scenario.id)
      .then((res) => {
        if (cancelled) return;
        const sorted = [...res.runs].sort(byStartedDesc);
        setRuns(sorted);
        const latest = latestByPath(sorted);
        setLegacyId(latest.legacy?.id ?? '');
        setSplitId(latest.split?.id ?? '');
      })
      .catch((e) => !cancelled && setUnavailable(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [scenario.id]);

  const byId = useMemo(() => new Map(runs.map((r) => [r.id, r])), [runs]);
  const legacyRun = byId.get(legacyId) ?? null;
  const splitRun = byId.get(splitId) ?? null;

  const removeRun = async (run: ScenarioRun) => {
    if (!confirm(`Delete this ${run.path} run from ${run.startedAt}?`)) return;
    try {
      await deleteScenarioRun(run.id);
      setRuns((prev) => prev.filter((r) => r.id !== run.id));
      if (parityRunId === run.id) setParityRunId(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete the run');
    }
  };

  /**
   * A run's parity report is the MERGE of one report per pebble the run
   * created: the route filters by a single `pebbleId`, and a run has one pebble
   * per side. Merging adds counts and recomputes the ratio (`mergeReports`) —
   * an average of the sides' ratios would not be the ratio of the union.
   */
  const openParity = async (run: ScenarioRun) => {
    if (parityRunId === run.id) {
      setParityRunId(null);
      return;
    }
    setParityRunId(run.id);
    setParity(null);
    setParityError(null);
    const pebbles = runPebbleIds(run);
    if (pebbles.length === 0) {
      setParityError('This run recorded no pebble id, so its parity rows cannot be found.');
      return;
    }
    setParityLoading(true);
    try {
      const reports = await Promise.all(
        pebbles.map((pebbleId) => getExtractedParity({ pebbleId, rows: 200 })),
      );
      setParity(mergeReports(reports));
    } catch (e) {
      setParityError(e instanceof Error ? e.message : String(e));
    } finally {
      setParityLoading(false);
    }
  };

  const parityRun = parityRunId ? byId.get(parityRunId) : null;

  return (
    <div className="space-y-4" data-testid="scenario-runs">
      {/* 1 · Launch */}
      <details className="border border-gray-200 rounded-lg p-3">
        <summary className="text-sm font-medium text-gray-800 cursor-pointer">
          Launch this scenario
        </summary>
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">
            The run itself drives the local stack and real models from your machine, so it is
            started there. The dashboard is where its result is read — file the receipt with the
            last command and this list picks it up.
          </p>
          {RUN_PATHS.map((path) => (
            <CopyBlock key={path} label={`${path} path`} text={launchCommands(scenario, path)} />
          ))}
          <CopyBlock label="record the run" text={recordRunCommand(scenario, 'split')} />
        </div>
      </details>

      {/* 2 · Read */}
      {loading && <p className="text-sm text-gray-500">Loading runs…</p>}

      {unavailable && !loading && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800"
          data-testid="runs-unavailable"
        >
          Runs could not be read: {unavailable}. If this says the table is missing, apply migration{' '}
          <span className="font-mono">033-scenario-runs.sql</span>. An empty list would have read as
          “never run”.
        </div>
      )}

      {!loading && !unavailable && runs.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="runs-empty">
          No runs recorded for this scenario yet.
        </p>
      )}

      {runs.length > 0 && (
        <div className="space-y-2">
          {runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              onDelete={removeRun}
              onParity={openParity}
              parityOpen={parityRunId === run.id}
            />
          ))}
        </div>
      )}

      {/* The selected run's parity report. */}
      {parityRun && (
        <ParityReport
          report={parity}
          unavailable={parityError}
          loading={parityLoading}
          title={`extracted_v1 parity — ${parityRun.path} run ${parityRun.startedAt}`}
          scope={`Scoped to ${runPebbleIds(parityRun).join(', ') || 'no pebble'}.`}
        />
      )}

      {/* 3 · Compare */}
      {runs.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-3 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-800">Legacy vs split</span>
            <label className="text-xs text-gray-600">
              legacy
              <select
                className="ml-1 border border-gray-300 rounded px-2 py-1 text-xs"
                value={legacyId}
                onChange={(e) => setLegacyId(e.target.value)}
                aria-label="legacy run"
              >
                <option value="">—</option>
                {runs
                  .filter((r) => r.path === 'legacy')
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.startedAt}
                    </option>
                  ))}
              </select>
            </label>
            <label className="text-xs text-gray-600">
              split
              <select
                className="ml-1 border border-gray-300 rounded px-2 py-1 text-xs"
                value={splitId}
                onChange={(e) => setSplitId(e.target.value)}
                aria-label="split run"
              >
                <option value="">—</option>
                {runs
                  .filter((r) => r.path === 'split')
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.startedAt}
                    </option>
                  ))}
              </select>
            </label>
            <button
              type="button"
              className="text-sm text-pebbl-700 hover:underline"
              onClick={() => setShowCompare((v) => !v)}
              disabled={!legacyRun || !splitRun}
            >
              {showCompare ? 'Hide comparison' : 'Compare runs'}
            </button>
            {(!legacyRun || !splitRun) && (
              <span className="text-xs text-gray-500" data-testid="compare-needs-both">
                needs one run on each path
              </span>
            )}
          </div>
          {showCompare && <PathComparison legacy={legacyRun} split={splitRun} />}
        </div>
      )}
    </div>
  );
}
